import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, useColorModeValue, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  MdDashboard,
  MdBusiness,
  MdLibraryMusic,
  MdQueueMusic,
  MdPeople,
  MdDescription,
  MdGavel,
  MdWarning,
  MdHistory,
  MdScience,
  MdArticle,
  MdMenuBook,
  MdViewCarousel,
  MdSupportAgent,
} from 'react-icons/md';
import { AuthProvider } from './features/auth/AuthProvider';
import { AuthGuard } from './components/AuthGuard';
import Sidebar, { type RouteItem } from './components/sidebar/Sidebar';
import { NavbarAdmin } from './components/navbar/NavbarAdmin';
import { LoginPage } from './features/auth/LoginPage';
import { CallbackPage } from './features/auth/CallbackPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { AdminUsersListPage } from './features/admin-users/AdminUsersListPage';
import { AdminUserForm } from './features/admin-users/AdminUserForm';
import { AdminUserDetailPage } from './features/admin-users/AdminUserDetailPage';
import { OrganizationsListPage } from './features/organizations/OrganizationsListPage';
import { OrganizationDetailPage } from './features/organizations/OrganizationDetailPage';
import { LibraryListPage } from './features/library/LibraryListPage';
import { MultiUploadPage } from './features/library/MultiUploadPage';
import { TrackDetailPage } from './features/library/TrackDetailPage';
import { LogViewerPage } from './features/logs/LogViewerPage';
import { PlaylistsListPage } from './features/playlists/PlaylistsListPage';
import { PlaylistForm } from './features/playlists/PlaylistForm';
import { PlaylistDetailPage } from './features/playlists/PlaylistDetailPage';
import { AuditLogPage } from './features/audit/AuditLogPage';
import { AlertsOverviewPage } from './features/alerts/pages/AlertsOverviewPage';
import { AlertsHistoryPage } from './features/alerts/pages/AlertsHistoryPage';
import { AlertSimulationPage } from './features/alerts/pages/AlertSimulationPage';
import { ContentListPage } from './features/content/ContentListPage';
import { ContentEditPage } from './features/content/ContentEditPage';
import { LicensesSettingsPage } from './features/licenses/LicensesSettingsPage';
import { DictionaryManagerPage } from './features/dictionary/DictionaryManagerPage';
import { PlaylistCollectionsPage } from './features/playlist-collections/PlaylistCollectionsPage';
import { CollectionDetailPage } from './features/playlist-collections/CollectionDetailPage';
import { CustomerRequestsListPage } from './features/support-requests/CustomerRequestsListPage';

function useRoutes(): RouteItem[] {
  const { t } = useTranslation();
  return [
    { name: t('admin.nav.dashboard'), path: '/dashboard', icon: <MdDashboard size={20} />, testId: 'nav-dashboard' },
    { name: t('admin.nav.organizations'), path: '/organizations', icon: <MdBusiness size={20} />, testId: 'nav-organizations' },
    { name: t('admin.nav.customerRequests'), path: '/customer-requests', icon: <MdSupportAgent size={20} />, testId: 'nav-customer-requests' },
    { name: t('admin.nav.library'), path: '/library', icon: <MdLibraryMusic size={20} />, testId: 'nav-library', roles: ['content_curator', 'system_admin'] },
    { name: t('admin.nav.curatedPlaylists'), path: '/curated-playlists', icon: <MdQueueMusic size={20} />, testId: 'nav-curated-playlists', roles: ['content_curator', 'system_admin'] },
    { name: t('admin.nav.playlistCollections'), path: '/playlist-collections', icon: <MdViewCarousel size={20} />, testId: 'nav-playlist-collections', roles: ['content_curator', 'system_admin'] },
    // Licenses are managed inside the Dictionaries page — no standalone nav item.
    { name: t('admin.nav.dictionary'), path: '/dictionary', icon: <MdMenuBook size={20} />, testId: 'nav-dictionary', roles: ['content_curator', 'system_admin'] },
    { name: t('admin.nav.adminUsers'), path: '/admin-users', icon: <MdPeople size={20} />, testId: 'nav-admin-users', roles: ['system_admin'] },
    { name: t('admin.nav.logs'), path: '/logs', icon: <MdDescription size={20} />, testId: 'nav-logs', roles: ['system_admin'] },
    { name: t('admin.nav.audit'), path: '/audit', icon: <MdGavel size={20} />, testId: 'nav-audit', roles: ['system_admin'] },
    // 014-alerts / US7 — overview + history visible to any platform admin;
    // the simulation page is SuperAdmin-only (Session 2026-04-18 Q2).
    { name: t('admin.nav.alertsOverview'), path: '/alerts', icon: <MdWarning size={20} />, testId: 'nav-alerts-overview' },
    { name: t('admin.nav.alertsHistory'), path: '/alerts/history', icon: <MdHistory size={20} />, testId: 'nav-alerts-history' },
    { name: t('admin.nav.alertsSimulate'), path: '/alerts/simulate', icon: <MdScience size={20} />, testId: 'nav-alerts-simulate', roles: ['system_admin'] },
    { name: t('admin.nav.content'), path: '/content', icon: <MdArticle size={20} />, testId: 'nav-content', roles: ['content_curator', 'system_admin'] },
  ];
}

function useBrandText(routes: RouteItem[]): string {
  const location = useLocation();
  const match = routes.find((r) => location.pathname.startsWith(r.path));
  return match?.name ?? '';
}

function AdminLayout() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const routes = useRoutes();
  const brandText = useBrandText(routes);
  const bg = useColorModeValue('secondaryGray.300', 'navy.900');

  return (
    <Box>
      <Sidebar routes={routes} isOpen={isOpen} onClose={onClose} />
      <Box
        float="right"
        minHeight="100vh"
        height="100%"
        overflow="auto"
        position="relative"
        maxHeight="100%"
        w={{ base: '100%', xl: 'calc( 100% - 285px )' }}
        maxWidth={{ base: '100%', xl: 'calc( 100% - 285px )' }}
        transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
        transitionDuration=".2s, .2s, .35s"
        transitionProperty="top, bottom, width"
        transitionTimingFunction="linear, linear, ease"
      >
        <NavbarAdmin brandText={brandText} onOpen={onOpen} />
        <Box
          mx="auto"
          p={{ base: '20px', md: '30px' }}
          pe="20px"
          minH="100vh"
          pt="100px"
          bg={bg}
        >
          <Box maxW="1440px" w="100%">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />

          {/* Protected routes */}
          <Route element={<AuthGuard><AdminLayout /></AuthGuard>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/organizations" element={<OrganizationsListPage />} />
            <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="/customer-requests" element={<CustomerRequestsListPage />} />
            <Route path="/library" element={<LibraryListPage />} />
            <Route path="/library/upload" element={<MultiUploadPage />} />
            <Route path="/library/:trackId" element={<TrackDetailPage />} />
            <Route path="/curated-playlists" element={<PlaylistsListPage />} />
            <Route path="/curated-playlists/new" element={<PlaylistForm />} />
            <Route path="/curated-playlists/:id" element={<PlaylistDetailPage />} />
            <Route path="/curated-playlists/:id/edit" element={<PlaylistForm />} />
            <Route path="/playlist-collections" element={<PlaylistCollectionsPage />} />
            <Route path="/playlist-collections/:slug" element={<CollectionDetailPage />} />
            <Route path="/admin-users" element={<AdminUsersListPage />} />
            <Route path="/admin-users/new" element={<AdminUserForm />} />
            <Route path="/admin-users/:id" element={<AdminUserDetailPage />} />
            <Route path="/admin-users/:id/edit" element={<AdminUserForm />} />
            <Route path="/logs" element={<LogViewerPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/alerts" element={<AlertsOverviewPage />} />
            <Route path="/alerts/history" element={<AlertsHistoryPage />} />
            <Route path="/alerts/simulate" element={<AlertSimulationPage />} />
            <Route path="/content" element={<ContentListPage />} />
            <Route path="/content/:id" element={<ContentEditPage />} />
            <Route path="/licenses" element={<LicensesSettingsPage />} />
            <Route path="/dictionary" element={<DictionaryManagerPage />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

